import Breadcrumb from "@/components/Breadcrumb"
import LimitPaginationComponent from "@/components/Table/LimitPaginationComponent"
import SearchComponent from "@/components/Table/SearchComponent"
import TableComponent from "@/components/Table/TabelComponent"
import { DataStatusRental, IDropdown, LIMIT_PAGINATE } from "@/constants/default"
import { dataQueryApi, dataQueryClient } from "@/utils/query"
import queryString from "query-string"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { createSearchParams, useLocation, useNavigate, useSearchParams } from "react-router-dom"
import TableHeader from "./_header"
import _ from "lodash"
import { usePermissions } from "@/rbac"
import { permissions } from "@/rbac/permissions"
import { useGetRentalsExpiredQuery, useGetRentalsQuery, useGetTenantExpiredQuery } from "@/services/rental.service"
import DropdownCheckbox from "@/components/Dropdown/DropdownCheckbox"
import TableHeaderExired, { TableHeaderFuture } from "./_header-expired"
import TableHeaderTenantExpired from "./_header-tenant-expired"
import { useGetRoomFreesQuery } from "@/services/dashboard.service"
import TableHeaderRoomFree from "../dashboard/_headerRoomFree"

const defaultSort: string = 'end_date'
const defaultOrder: string = "desc"

export default function Rentals() {
    const { t } = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()
    const parsed = queryString.parse(location?.search)
    const DataStatus = DataStatusRental()
    const limit = _.isString(parsed.l) ? parseInt(parsed?.l) : LIMIT_PAGINATE
    const {data: rentals, refetch, isFetching, isLoading} = useGetRentalsQuery(dataQueryApi(location?.search, {limit, field: 'end_date', status: Object.hasOwn(parsed, 'status') ? parsed.status : "2,3"}), {refetchOnMountOrArgChange: true})
    const {hasPermission} = usePermissions()
    const {data: rentalExpired, refetch: refetchRentalExpired, isFetching: isFetchingRentalExpired, isLoading: isLoadingRentalExpired} = useGetRentalsExpiredQuery({}, {refetchOnFocus: true, refetchOnMountOrArgChange: true, refetchOnReconnect: true})
    const {data: tenantExpired, refetch: refetchTenantExpired, isFetching: isFetchingTenantExpired, isLoading: isLoadingTenantExpired} = useGetTenantExpiredQuery({}, {refetchOnFocus: true, refetchOnMountOrArgChange: true, refetchOnReconnect: true})
    const {data: roomFrees, refetch: refetchRoomFrees, isFetching: isFetchingRoomFree} = useGetRoomFreesQuery({}, {refetchOnMountOrArgChange: true, refetchOnFocus: true, refetchOnReconnect: true})

    const getData = (type: string, result: any) => {
        let data = dataQueryClient(location.search, {field: 'end_date'})
        data.status = Object.hasOwn(parsed, 'status') ? parsed.status : "2,3,4"
        switch (type) {
            case "search":
                data.s = result
                data.p = "1"
                break
            case "sort":
                data.o = result[0].desc ? "desc" : "asc"
                data.f = result[0].id
                break
            case "page":
                data.p = result
                break
            case "limit":
                data.l = result
                data.p = "1"
                break;
            case "status":
                data.status = result.map((s: IDropdown) => s.value).join(",")
                break
            default:
                data = { ...data };
                break;
        }
        navigate({
            search: createSearchParams(data).toString()
        })
    }

    useEffect(() => {
        if(location.state?.reload) {
            if (rentals?.data?.length === 1 && Number(parsed.p) !== 1) {
                getData("page", 1)
            } else {
                refetch()
                refetchRentalExpired()
            }
        }
    }, [location.state?.reload])

    const renderTotalRooms = (total: number) => {
        let room = t("rental.room").toLocaleLowerCase()
        if (total === 0) {
            return ""
        } else if (total > 1) {
            room = t("rental.rooms").toLocaleLowerCase()
        }
        return ` ( ${total} ${room} )`
    }

    const renderTotalTenants = (total: number) => {
        let tenant = t("rental.tenant").toLocaleLowerCase()
        if (total === 0) {
            return ""
        } else if (total > 1) {
            tenant = t("rental.tenants").toLocaleLowerCase()
        }
        return ` ( ${total} ${tenant} )`
    }

    return (
        <>
            <Breadcrumb pageName={t("rental.customer_vise_expiring")} />
            <div className="flex flex-col">
                <div className="rounded-sm border border-stroke bg-white px-5 pt-5 pb-5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-5 xl:pb-4 mb-6">
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                        <div className="max-w-full overflow-x-auto mt-3">
                            <p className="h-9 bg-danger flex items-center ps-2 font-semibold dark:text-white">{`${t("rental.expired_month")}${renderTotalTenants(tenantExpired?.month?.length || 0)}`}</p>
                            <TableComponent
                                data={tenantExpired?.month || []}
                                columns={TableHeaderTenantExpired()}
                                isLoading={isFetchingTenantExpired || isLoadingTenantExpired}
                            />
                        </div>
                        <div className="max-w-full overflow-x-auto mt-3">
                            <p className="h-9 bg-warning flex items-center ps-2 font-semibold dark:text-white">{`${t("rental.expired_2months")}${renderTotalTenants(tenantExpired?.two_months?.length || 0)}`}</p>
                            <TableComponent
                                data={tenantExpired?.two_months || []}
                                columns={TableHeaderTenantExpired()}
                                isLoading={isFetchingTenantExpired || isLoadingTenantExpired}
                            />
                        </div>
                        <div className="max-w-full overflow-x-auto mt-3">
                            <p className="h-9 bg-yellow-300 flex items-center ps-2 font-semibold dark:text-white">{`${t("rental.expired_3months")}${renderTotalTenants(tenantExpired?.three_months?.length || 0)}`}</p>
                            <TableComponent
                                data={tenantExpired?.three_months || []}
                                columns={TableHeaderTenantExpired()}
                                isLoading={isFetchingTenantExpired || isLoadingTenantExpired}
                            />
                        </div>
                    </div>
                </div>
                <h2 className="text-title-md2 font-semibold mb-3">
                    {t("rental.rentals")}
                </h2>
                <div className="rounded-sm border border-stroke bg-white px-5 pt-5 pb-5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-5 xl:pb-4">
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 mb-10">
                        <div className="max-w-full overflow-x-auto mt-3">
                            <p className="h-9 bg-danger flex items-center ps-2 font-semibold dark:text-white">{`${t("rental.expired_month")}${renderTotalRooms(rentalExpired?.month?.length || 0)}`}</p>
                            <TableComponent
                                data={rentalExpired?.month || []}
                                columns={TableHeaderExired()}
                                isLoading={isFetchingRentalExpired || isLoadingRentalExpired}
                            />
                        </div>
                        <div className="max-w-full overflow-x-auto mt-3">
                            <p className="h-9 bg-warning flex items-center ps-2 font-semibold dark:text-white">{`${t("rental.expired_2months")}${renderTotalRooms(rentalExpired?.two_months?.length || 0)}`}</p>
                            <TableComponent
                                data={rentalExpired?.two_months || []}
                                columns={TableHeaderExired()}
                                isLoading={isFetchingRentalExpired || isLoadingRentalExpired}
                            />
                        </div>
                        <div className="max-w-full overflow-x-auto mt-3">
                            <p className="h-9 bg-yellow-300 flex items-center ps-2 font-semibold dark:text-white">{`${t("rental.expired_3months")}${renderTotalRooms(rentalExpired?.three_months?.length || 0)}`}</p>
                            <TableComponent
                                data={rentalExpired?.three_months || []}
                                columns={TableHeaderExired()}
                                isLoading={isFetchingRentalExpired || isLoadingRentalExpired}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 mb-10">
                        <div className="max-w-full overflow-x-auto mt-3">
                            <p className="h-9 bg-cyan-500 flex items-center ps-2 font-semibold dark:text-white">{`${t("rental.future_rental")}${renderTotalRooms(rentalExpired?.futures?.length || 0)}`}</p>
                            <TableComponent
                                data={rentalExpired?.futures || []}
                                columns={TableHeaderFuture()}
                                isLoading={isFetchingRentalExpired || isLoadingRentalExpired}
                            />
                        </div>
                        <div className="max-w-full overflow-x-auto mt-3">
                            <p className="h-9 bg-purple-500 flex items-center ps-2 font-semibold dark:text-white">{`${t("dashboard.vacant_rooms")}${renderTotalRooms(roomFrees?.length || 0)}`}</p>
                            <TableComponent
                                data={roomFrees || []}
                                columns={TableHeaderRoomFree()}
                                isLoading={isFetchingRoomFree}
                                titleNone="No vacant rooms"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
                        <div className="flex xs:flex-col flex-row col-span-2">
                            <LimitPaginationComponent limit={limit} onChange={(s: number) => getData("limit", s)} />
                            <SearchComponent search={parsed?.s?.toString() || ""} onChange={(s) => getData("search", s)} />
                            <div className="ms-4">
                                <DropdownCheckbox data={DataStatus} onFilter={(selected) => getData("status", selected)} selected={DataStatus.filter((s) => (Object.hasOwn(parsed, 'status') ? parsed.status : "2,3,4")?.toString().split(",").includes(s.value))}/>
                            </div>
                        </div>
                       
                        <div className="flex justify-start md:justify-end mt-2 md:mt-0">
                            <div className="me-2">
                                <button className="flex w-full justify-center items-center rounded bg-primary py-1.5 px-2 font-medium text-white text" onClick={() => {
                                    refetch()
                                    refetchRentalExpired()
                                    refetchTenantExpired()
                                }}><i className="fa-sharp fa-light fa-arrows-rotate me-2 text-white" />{t("table.reload")}</button>
                            </div>
                            {
                                hasPermission(permissions.RENTAL.ADD) && <div>
                                    <button className="flex w-full justify-center items-center rounded bg-primary py-1.5 px-2 font-medium text-white text" onClick={() => navigate(location, { state: { modal: "/rentals/add" } })}><i className="fa-light fa-plus me-2 text-white" />{t("table.add")}</button>
                                </div>
                            }
                        </div>
                    </div>

                    <div className="max-w-full overflow-x-auto mt-3">
                        <TableComponent
                            data={rentals?.data || []}
                            columns={TableHeader()}
                            pageSize={limit}
                            isPagination={true}
                            currentPage={_.isString(parsed?.p) ? parseInt(parsed?.p) - 1 : 0}
                            totalPages={Math.ceil((rentals?.total_records || 0) / limit)}
                            onChangePage={(page: number) => getData("page", page + 1)}
                            isLoading={isFetching || isLoading}
                            sort={[
                                {
                                    id: parsed?.f?.toString() || defaultSort,
                                    desc: parsed?.o === defaultOrder ? true : false || true
                                }
                            ]}
                            onChangeSort={data => getData("sort", data)}
                        />
                    </div>
                </div>
            </div>
        </>
    )
}
